import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const WorkExperience = () => {
  const [expandedItem, setExpandedItem] = useState(null);

  const experiences = [
    {
      id: 1,
      company: "",
      logo: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=80&h=80&fit=crop",
      position: "Senior Full-Stack Developer",
      duration: "2022 - Present",
      location: "Lagos, NG",
      type: "Full-time",
      description: `Leading a team of 8 developers in building next-generation SaaS platforms. Responsible for architecture decisions, code reviews, and mentoring junior developers.`,
      achievements: [
        "Increased application performance by 40% through optimization",
        "Led migration to microservices architecture",
        "Implemented CI/CD pipeline reducing deployment time by 60%",
        "Mentored 6 junior developers, 4 received promotions"
      ],
      technologies: ["React", "Node.js", "PostgreSQL", "AWS", "Docker", "Kubernetes"]
    },
    {
      id: 2,
      company: "",
      logo: "https://images.unsplash.com/photo-1572021335469-31706a17aaef?w=80&h=80&fit=crop",
      position: "Full-Stack Developer",
      duration: "2020 - 2022",
      location: "",
      type: "Full-time",
      description: `Developed and maintained multiple client applications from concept to deployment. Worked directly with clients to understand requirements and deliver solutions.`,
      achievements: [
        "Built 12+ client applications with 99.9% uptime",
        "Reduced client onboarding time by 50%",
        "Implemented real-time features serving 100K+ users",
        "Achieved 95% client satisfaction rating"
      ],
      technologies: ["Vue.js", "Express.js", "MongoDB", "Firebase", "Stripe API"]
    },
    {
      id: 3,
      company: "",
      logo: "https://images.unsplash.com/photo-1551836022-deb4988cc6c0?w=80&h=80&fit=crop",
      position: "Frontend Developer",
      duration: "2018 - 2020",
      location: "",
      type: "Full-time",
      description: `Focused on creating responsive, accessible user interfaces for e-commerce platforms. Collaborated with UX designers and backend developers.`,
      achievements: [
        "Improved conversion rates by 25% through UX optimization",
        "Implemented accessibility standards (WCAG 2.1)",
        "Reduced page load times by 35%",
        "Led frontend architecture for 3 major projects"
      ],
      technologies: ["React", "TypeScript", "SASS", "Webpack", "Jest"]
    },
    {
      id: 4,
      company: "",
      logo: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=80&h=80&fit=crop",
      position: "Junior Developer",
      duration: "2016 - 2018",
      location: "",
      type: "Full-time",
      description: `Started my professional journey building websites and web applications for small to medium businesses. Gained experience in full development lifecycle.`,
      achievements: [
        "Delivered 20+ websites on time and within budget",
        "Learned modern development practices and tools",
        "Contributed to agency\'s first mobile app",
        "Received \'Rising Star\' award in 2017"
      ],
      technologies: ["HTML5", "CSS3", "JavaScript", "PHP", "MySQL", "WordPress"]
    }
  ];

  const toggleExpanded = (id) => {
    setExpandedItem(expandedItem === id ? null : id);
  };

  return (
    <section className="bg-background py-16">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
            Work Experience
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            A journey through my professional career, highlighting key roles and achievements
          </p>
        </div>

        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-border hidden md:block"></div>

          <div className="space-y-8">
            {experiences?.map((exp, index) => (
              <div key={exp?.id} className="relative">
                {/* Timeline Dot */}
                <div className="absolute left-6 w-4 h-4 bg-primary rounded-full border-4 border-surface shadow-subtle hidden md:block"></div>

                {/* Experience Card */}
                <div className="md:ml-16 bg-surface rounded-xl shadow-subtle border border-border overflow-hidden hover:shadow-moderate transition-all duration-200">
                  <div 
                    className="p-6 cursor-pointer"
                    onClick={() => toggleExpanded(exp?.id)}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                          <Image
                            src={exp?.logo}
                            alt={`${exp?.company} logo`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div>
                          <h3 className="text-xl font-semibold text-foreground">
                            {exp?.position}
                          </h3>
                          <p className="text-primary font-medium">{exp?.company}</p>
                          <div className="flex items-center space-x-4 text-sm text-muted-foreground mt-1">
                            <span>{exp?.duration}</span>
                            <span>•</span>
                            <span>{exp?.location}</span>
                            <span>•</span>
                            <span className="px-2 py-1 bg-muted rounded-md text-xs">
                              {exp?.type}
                            </span>
                          </div>
                        </div>
                      </div>
                      <Icon 
                        name={expandedItem === exp?.id ? "ChevronUp" : "ChevronDown"}
                        size={20}
                        className="text-muted-foreground transition-transform duration-200"
                      />
                    </div>

                    <p className="text-muted-foreground mb-4">
                      {exp?.description}
                    </p>

                    {/* Technology Tags */}
                    <div className="flex flex-wrap gap-2">
                      {exp?.technologies?.slice(0, 4)?.map((tech, techIndex) => (
                        <span 
                          key={techIndex}
                          className="px-3 py-1 bg-primary/10 text-primary text-sm rounded-full"
                        >
                          {tech}
                        </span>
                      ))}
                      {exp?.technologies?.length > 4 && (
                        <span className="px-3 py-1 bg-muted text-muted-foreground text-sm rounded-full">
                          +{exp?.technologies?.length - 4} more
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {expandedItem === exp?.id && (
                    <div className="px-6 pb-6 border-t border-border animate-slide-down">
                      <div className="pt-6">
                        <h4 className="font-semibold text-foreground mb-3">
                          Key Achievements
                        </h4>
                        <ul className="space-y-2 mb-6">
                          {exp?.achievements?.map((achievement, achIndex) => (
                            <li key={achIndex} className="flex items-start space-x-3">
                              <Icon 
                                name="CheckCircle" 
                                size={16} 
                                className="text-success mt-0.5 flex-shrink-0"
                              />
                              <span className="text-muted-foreground text-sm">
                                {achievement}
                              </span>
                            </li>
                          ))}
                        </ul>

                        <h4 className="font-semibold text-foreground mb-3">
                          Technologies Used
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {exp?.technologies?.map((tech, techIndex) => (
                            <span 
                              key={techIndex}
                              className="px-3 py-1 bg-accent/10 text-accent text-sm rounded-full"
                            >
                              {tech}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default WorkExperience;